/*
 * EuroFarm i18n — Translations
 * =============================
 *
 * Current languages: ru (Русский), en (English), ro (Română)
 *
 * ==== HOW TO ADD A NEW LANGUAGE ====
 *
 * 1. Add the language code to the `Lang` type:
 *      export type Lang = "ru" | "en" | "ro" | "de";
 *
 * 2. Add it to LANGUAGES array:
 *      { code: "de", label: "De" }
 *
 * 3. Add a new block in `translations` with ALL existing keys:
 *      de: {
 *        "nav.home": "Startseite",
 *        "nav.about": "Über uns",
 *        ...
 *      }
 *
 *    Pro tip: copy the `en` block, paste it, rename to the new code,
 *    then translate each value line by line.
 *
 * 4. That's it — the language appears in the Nav dropdown automatically.
 *    No component changes needed.
 *
 * ==== HOW TO ADD A NEW TRANSLATION KEY ====
 *
 * 1. Add the key to ALL language blocks in `translations`.
 * 2. Use it in a component:  const { t } = useLang();  {t("my.new.key")}
 * 3. If the key is missing in the current language, it falls back to Russian.
 *
 * ==== NAMING CONVENTION ====
 * Keys use dot notation: "section.element"  (e.g. "hero.title_join", "nav.home")
 */

export type Lang = "ru" | "en" | "ro";

export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: "ru", label: "Ru" },
  { code: "en", label: "En" },
  { code: "ro", label: "Ro" },
];

const translations: Record<Lang, Record<string, string>> = {
  ru: {
    /* ---- Nav ---- */
    "nav.home": "Главная",
    "nav.about": "О платформе",
    "nav.farmers": "Фермерам",
    "nav.buyers": "Покупателям",
    "nav.logistics": "Логистика",
    "nav.academy": "Академия",
    "nav.contacts": "Контакты",
    "nav.login": "Войти",
    "nav.menu_label": "Главное меню",

    /* ---- Hero ---- */
    "hero.breadcrumb": "Международная агроплатформа EuroFarm • Молдова → Европа",
    "hero.title_join": "Присоединяйтесь к",
    "hero.title_sell": "и продавайте продукцию",
    "hero.title_region": "по всей территории",
    "hero.title_moldova": "Молдовы и Европы",
    "hero.subtitle": "Цифровая экспортная платформа для фермеров Молдовы",
    "hero.cta_primary": "Разместить продукцию",
    "hero.cta_secondary": "Узнать больше",

    /* ---- Hero Stats ---- */
    "hero.stat.farmers": "фермеров на платформе",
    "hero.stat.countries": "стран-покупателей",
    "hero.stat.exported": "экспортировано продукции",
    "hero.stat.support": "поддержка фермеров",

    /* ---- Hero Features ---- */
    "hero.feat.europe": "Выход на\nрынок Европы",
    "hero.feat.translate": "AI-перевод\nкарточек",
    "hero.feat.export": "Помощь\nс экспортом",
    "hero.feat.logistics": "Логистика\nNova Post",
    "hero.feat.safe": "Защита\nсделок",

    /* ---- Slider ---- */
    "slider.prev": "Назад",
    "slider.next": "Вперёд",
    "slider.slide": "Слайд",
  },

  en: {
    "nav.home": "Home",
    "nav.about": "About",
    "nav.farmers": "For Farmers",
    "nav.buyers": "For Buyers",
    "nav.logistics": "Logistics",
    "nav.academy": "Academy",
    "nav.contacts": "Contacts",
    "nav.login": "Sign in",
    "nav.menu_label": "Main menu",

    "hero.breadcrumb": "International agro platform EuroFarm • Moldova → Europe",
    "hero.title_join": "Join",
    "hero.title_sell": "and sell your produce",
    "hero.title_region": "across",
    "hero.title_moldova": "Moldova and Europe",
    "hero.subtitle": "Digital export platform for Moldovan farmers",
    "hero.cta_primary": "List your produce",
    "hero.cta_secondary": "Learn more",

    "hero.stat.farmers": "farmers on the platform",
    "hero.stat.countries": "buying countries",
    "hero.stat.exported": "products exported",
    "hero.stat.support": "farmer support",

    "hero.feat.europe": "Access to\nEuropean market",
    "hero.feat.translate": "AI translation\nof listings",
    "hero.feat.export": "Export\nassistance",
    "hero.feat.logistics": "Nova Post\nlogistics",
    "hero.feat.safe": "Deal\nprotection",

    "slider.prev": "Back",
    "slider.next": "Forward",
    "slider.slide": "Slide",
  },

  ro: {
    "nav.home": "Acasă",
    "nav.about": "Despre",
    "nav.farmers": "Fermieri",
    "nav.buyers": "Cumpărători",
    "nav.logistics": "Logistică",
    "nav.academy": "Academie",
    "nav.contacts": "Contacte",
    "nav.login": "Intră",
    "nav.menu_label": "Meniu principal",

    "hero.breadcrumb": "Platforma agro internațională EuroFarm • Moldova → Europa",
    "hero.title_join": "Alătură-te",
    "hero.title_sell": "și vinde producția",
    "hero.title_region": "pe întreg teritoriul",
    "hero.title_moldova": "Moldovei și Europei",
    "hero.subtitle": "Platformă digitală de export pentru fermierii din Moldova",
    "hero.cta_primary": "Publică producția",
    "hero.cta_secondary": "Află mai mult",

    "hero.stat.farmers": "fermieri pe platformă",
    "hero.stat.countries": "țări cumpărătoare",
    "hero.stat.exported": "produse exportate",
    "hero.stat.support": "suport pentru fermieri",

    "hero.feat.europe": "Acces pe\npiața europeană",
    "hero.feat.translate": "Traducere AI\na cartelelor",
    "hero.feat.export": "Asistență\nla export",
    "hero.feat.logistics": "Logistică\nNova Post",
    "hero.feat.safe": "Protecția\ntranzacțiilor",

    "slider.prev": "Înapoi",
    "slider.next": "Înainte",
    "slider.slide": "Slide",
  },
};

export function t(lang: Lang, key: string): string {
  return translations[lang]?.[key] ?? translations["ru"]?.[key] ?? key;
}
